// Контакты для списков рассылки: клиенты берутся из таблицы компаний основной системы
// (директор + телефон), к ним добавляются три тестовых номера. Согласие на рассылку
// хранит сервер (ответы клиентов, СТОП, ручные отметки), см. withConsents.

// Чем подтверждается согласие. Meta требует не только сам факт согласия,
// но и возможность показать, откуда оно взялось.
export const CONSENT_SOURCES = ['Договор', 'Анкета', 'Согласие по телефону'];

// Статус подтверждения согласия на рассылку. kind задаёт цвет значка.
// «Не запрашивали» — согласия нет и клиента о нём ещё не спрашивали.
export const CONSENT_STATUS = {
  none: { label: 'Не запрашивали', kind: 'idle' },
  pending: { label: 'Ждём ответа', kind: 'warn' },
  confirmed: { label: 'Подтверждён', kind: 'ok' },
  declined: { label: 'Отказался', kind: 'error' },
};

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

// Накладывает на контакты то, что известно серверу о согласии (ответы клиентов на
// приветственную рассылку, СТОП, ручные отметки). Контакт, о котором сервер ничего
// не знает, остаётся как в исходной базе: согласие по договору или «не запрашивали».
// Молчащему клиенту запрос согласия можно повторить: не чаще раза в REASK_DAYS дней и не
// больше MAX_ASKS запросов всего. Те же числа проверяет сервер.
export const MAX_ASKS = 3;
export const REASK_DAYS = 3;

export const canReask = (contact, now = new Date()) => {
  if (contact.consentStatus !== 'pending') return false;
  if ((contact.consentAsks || 1) >= MAX_ASKS) return false;
  if (!contact.consentWelcomeAt) return true;
  return now - new Date(contact.consentWelcomeAt) >= REASK_DAYS * 24 * 3600 * 1000;
};

export const withConsents = (base, rows) => {
  const byPhone = {};
  rows.forEach((row) => {
    byPhone[row.phone] = row;
  });
  return base.map((contact) => {
    const row = byPhone[digitsOnly(contact.phone)];
    if (!row) {
      return {
        ...contact,
        consentStatus: contact.optIn ? 'confirmed' : 'none',
        consentReply: '',
        consentWelcomeAt: null,
        consentAsks: 0,
        consentAt: null,
        consentBy: '',
        consentRecorded: false,
      };
    }
    return {
      ...contact,
      optIn: row.status === 'confirmed',
      consentStatus: row.status,
      consentSource: row.source,
      // Ответ клиента сам по себе доказательство: его текст и время записаны.
      consentEvidence:
        row.evidence || (row.updatedBy === 'client' ? 'ответ клиента в WhatsApp' : ''),
      consentReply: row.replyText,
      consentWelcomeAt: row.welcomeSentAt,
      consentAsks: row.welcomeCount || 0,
      consentAt: row.confirmedAt,
      consentBy: row.updatedBy,
      consentRecorded: true,
    };
  });
};

// Один номер так, как его принимает WhatsApp: 380 и девять цифр.
const normalizeOne = (raw) => {
  let digits = digitsOnly(raw);
  if (digits.length === 10 && digits[0] === '0') digits = `38${digits}`;
  else if (digits.length === 11 && digits.startsWith('80')) digits = `3${digits}`;
  return /^380\d{9}$/.test(digits) ? digits : '';
};

// Номер из ячейки компании. Приводит то, что в основной системе набрано как угодно
// (+38 (050) 111-22-33, 0501112233, 80501112233). Если в ячейке несколько номеров (через
// запятую, точку с запятой, слэш или два пробела), берётся первый пригодный. Если ни один
// не подходит, возвращает пустую строку: такому контакту писать нельзя.
export const normalizePhone = (raw) => {
  const parts = String(raw || '').split(/[,;/]|\s{2,}|\s+(?=\+)/);
  for (let i = 0; i < parts.length; i += 1) {
    const phone = normalizeOne(parts[i]);
    if (phone) return phone;
  }
  return '';
};

const formatPhone = (digits) =>
  `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(
    8,
    10
  )} ${digits.slice(10, 12)}`;

// Компании основной системы -> контакты. Компании без подходящего номера в рассылку не
// попадают, их число и названия возвращаются отдельно (по названию менеджер находит компанию и
// исправляет номер). Список компаний приходит вместе с
// уведомлениями, поэтому одна компания может встретиться несколько раз.
export const contactsFromCompanies = (rows) => {
  const seen = new Set();
  const contacts = [];
  const withoutPhoneCompanies = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || seen.has(row.id)) return;
    seen.add(row.id);
    const phone = normalizePhone(row.phoneNumber);
    if (!phone) {
      withoutPhoneCompanies.push({ id: row.id, name: row.name || '', phone: row.phoneNumber || '' });
      return;
    }
    contacts.push({
      id: row.id,
      name: row.director || row.name || '—',
      company: row.name || '',
      phone: formatPhone(phone),
      type: row.type || 'Не указан',
      region: row.region || row.state || '',
      optIn: false,
      consentSource: '',
      consentEvidence: '',
    });
  });
  return { contacts, withoutPhone: withoutPhoneCompanies.length, withoutPhoneCompanies };
};

export const DEFAULT_LIST_IDS = ['all', 'farmers', 'factories', 'brokers'];

// Списки, с которыми менеджер начинает работу, из клиентов основной системы.
export const defaultListsFor = (contacts) => {
  const real = contacts;
  const byType = (type) => real.filter((contact) => contact.type === type).map((contact) => contact.id);
  return [
    { id: 'all', name: 'Все клиенты', contactIds: real.map((contact) => contact.id) },
    { id: 'farmers', name: 'Сельхозпроизводители', contactIds: byType('Сельхозпроизводитель') },
    { id: 'factories', name: 'Заводы', contactIds: byType('Завод') },
    { id: 'brokers', name: 'Посредники', contactIds: byType('Посредник') },
  ];
};

// Сколько в списке контактов и сколько из них получат сообщение.
export const listStats = (list, contacts) => {
  const ids = {};
  list.contactIds.forEach((id) => {
    ids[id] = true;
  });
  const inList = contacts.filter((contact) => ids[contact.id]);
  return {
    total: inList.length,
    optIn: inList.filter((contact) => contact.optIn).length,
  };
};
